import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeModules } from 'react-native';

import { ContactSupport } from '@/components/ContactSupport';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Box } from '@/components/lib/components';
import { queryClient } from '@/data/reactQueryClient';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { TrustlyKycResponse, useTrustlyKyc } from '@/feature/kyc/hooks/use-trustly-kyc';
import { useTrustlyKycStore } from '@/feature/kyc/hooks/use-trustly-kyc-store';
import { getPreviousRouteName } from '@/feature/kyc/screens/Idpv/utils';
import { selectAllWithdrawMethods, useWithdrawalMethods } from '@/feature/withdraw/hooks/use-withdrawal-methods';
import { getLatestTrustlyMethod } from '@/feature/withdraw/utils/utils';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { trustlyInfoKeys } from '@/hooks/use-user-trustly-information';
import { KycStackScreenProps } from '@/navigation/kyc/types';
import { kycProceedToFinancialStack, popToHome } from '@/navigation/navigation';
import { read } from '@/utils/async-storage';
import { logger } from '@/utils/logging';

const { PaymentsWebView } = NativeModules;

type TrustlyKycStatusProps = KycStackScreenProps<'TrustlyKycStatusModal'>;

export const TrustlyKycStatus = ({ navigation, route }: TrustlyKycStatusProps) => {
    const { currentStatus } = route.params;
    const { t } = useTranslation(['kyc', 'common']);
    const isTrustlyProcessingRef = useRef(false);
    const tSocialWallet = useSocialWalletTranslation(['wallet', 'kyc']);
    const { refetch: refetchWithdrawalMethods } = useWithdrawalMethods({
        select: selectAllWithdrawMethods,
    });
    const { mutate: triggerTrustlyKycOnly } = useTrustlyKyc();
    const shouldProceedToWithdrawal = useTrustlyKycStore(s => s.shouldProceedToWithdrawal);

    const rootNavigation = useRootNavigation();
    const navigateToAccount = useCallback(() => popToHome(rootNavigation, 'account'), [rootNavigation]);

    const handleTrustlyVerificationError = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_FAILED);
        navigation.navigate('ErrorModal', {
            title: t('trustly_verification_failed_title'),
            subtitle: t('trustly_kyc_only_failure_description'),
            primaryButton: t('common:return_to_account'),
            handlePress: navigateToAccount,
            showNavbarTitle: false,
            showSecondaryButton: false,
            closeIconHandlePress: navigateToAccount,
            footer: <ContactSupport invokingScreen="TrustlyKycStatusModal" />,
        });
    }, [navigation, navigateToAccount, t]);

    const handleTrustlyVerificationSuccess = useCallback(async () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_SUCCESS);
        // home isn't remounted, so refetch what the Trustly verification changed (link bank, withdraw gate)
        queryClient.refetchQueries({ queryKey: trustlyInfoKeys.all });

        if (!shouldProceedToWithdrawal) {
            navigation.navigate('SuccessModal', {
                title: t('trustly_verification_successful_title'),
                subtitle: tSocialWallet('kyc:trustly_kyc_only_successful_without_withdrawal_description'),
                primaryButton: t('common:return_to_account'),
                handlePress: navigateToAccount,
                closeIconHandlePress: navigateToAccount,
            });
            return;
        }

        const { data: freshWithdrawMethods } = await refetchWithdrawalMethods();
        navigation.navigate('SuccessModal', {
            title: t('trustly_verification_successful_title'),
            subtitle: tSocialWallet('kyc:trustly_kyc_only_successful_description'),
            primaryButton: tSocialWallet('proceed_to_withdrawal'),
            handlePress: () => {
                const latestTrustlyMethod = getLatestTrustlyMethod(freshWithdrawMethods ?? []);

                if (!latestTrustlyMethod) {
                    navigation.navigate('ErrorModal', {
                        title: t('trustly_verification_failed_title'),
                        subtitle: t('trustly_kyc_only_failure_description'),
                        primaryButton: t('common:return_to_account'),
                        handlePress: navigateToAccount,
                        closeIconHandlePress: navigateToAccount,
                    });
                    return;
                }

                kycProceedToFinancialStack(navigation, {
                    name: 'WithdrawStack',
                    params: {
                        screen: 'WithdrawForm',
                        params: {
                            selectedMethod: latestTrustlyMethod,
                            methods: freshWithdrawMethods || [],
                        },
                    },
                });
            },
            secondaryButton: t('common:return_to_account'),
            handleSecondaryPress: navigateToAccount,
            closeIconHandlePress: navigateToAccount,
        });
    }, [navigation, navigateToAccount, t, tSocialWallet, refetchWithdrawalMethods, shouldProceedToWithdrawal]);

    const handleTrustlyKycTransaction = useCallback(async () => {
        const transactionId = (await read('trustlyTransactionId')) ?? '';

        if (!transactionId) {
            navigation.navigate('ErrorModal', {
                title: t('trustly_verification_failed_title'),
                subtitle: t('trustly_kyc_only_failure_description'),
                primaryButton: t('common:return_to_account'),
                handlePress: navigateToAccount,
                closeIconHandlePress: navigateToAccount,
            });
            return;
        }

        if (getPreviousRouteName(navigation, route).includes('PickemHome')) {
            popToHome(rootNavigation, 'lobby');
            return;
        }

        triggerTrustlyKycOnly(
            {
                transactionId,
            },
            {
                onSuccess: (responseData: TrustlyKycResponse) => {
                    if (!responseData.verification_success) {
                        return handleTrustlyVerificationError();
                    }
                    return handleTrustlyVerificationSuccess();
                },
                onError: handleTrustlyVerificationError,
            }
        );
    }, [
        triggerTrustlyKycOnly,
        navigation,
        rootNavigation,
        handleTrustlyVerificationError,
        handleTrustlyVerificationSuccess,
        t,
        route,
        navigateToAccount,
    ]);

    useEffect(() => {
        return () => {
            useTrustlyKycStore.getState().resetShouldProceedToWithdrawal();
        };
    }, []);

    useEffect(() => {
        const processKycLink = async () => {
            try {
                PaymentsWebView?.closeWebView();
            } catch (err) {
                logger.warn('Unable to close webview: ', err);
            }

            if (currentStatus === 'trustly_kyc_only_success') {
                if (!isTrustlyProcessingRef.current) {
                    isTrustlyProcessingRef.current = true;
                    await handleTrustlyKycTransaction();
                }
            } else if (currentStatus === 'trustly_kyc_only_failure') {
                isTrustlyProcessingRef.current = false;
                handleTrustlyVerificationError();
            } else {
                isTrustlyProcessingRef.current = false;
                popToHome(rootNavigation, 'lobby');
            }
        };

        processKycLink();
    }, [currentStatus, rootNavigation, handleTrustlyKycTransaction, handleTrustlyVerificationError]);

    return (
        <Box bg="neutral900" flex={1} justifyContent="center">
            <LoadingSpinner />
        </Box>
    );
};
